import React, { useMemo } from 'react';
import { ReactComponent as BackArrowIcon } from '../../Assets/icon/arrow-narrow-left.svg';
import { ReactComponent as CalmDownIcon } from '../../Assets/icon/calm-down.svg';
import { ReactComponent as RelationshipsIcon } from '../../Assets/icon/relationships.svg';
import { ReactComponent as MeditationIcon } from '../../Assets/icon/meditation_icon.svg';
import './styles.css';

// Category color mapping
const CATEGORY_COLORS = {
  'calm down': '#46A978',
  'calm_down': '#46A978',
  'mental': '#46A978',
  'motivate me': '#DE6100',
  'motivate_me': '#DE6100',
  'motivation': '#DE6100',
  'physical': '#DE6100',
  'relationships': '#0089DE',
  'relationship': '#0089DE',
};

// Motivate Me Icon component
const MotivateIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 50 50" fill="none">
    <path d="M25.0001 32.2917H15.6251C12.7177 32.2917 11.2639 32.2917 10.081 32.6505C7.4177 33.4584 5.3335 35.5426 4.52558 38.206C4.16675 39.3889 4.16675 40.8426 4.16675 43.75M33.3334 37.5L37.5001 41.6667L45.8334 33.3333M30.2084 15.625C30.2084 20.8027 26.0111 25 20.8334 25C15.6557 25 11.4584 20.8027 11.4584 15.625C11.4584 10.4473 15.6557 6.25 20.8334 6.25C26.0111 6.25 30.2084 10.4473 30.2084 15.625Z" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// Get icon component based on category/topic
const getCategoryIcon = (categoryName, topicTitle) => {
  const lowerCategory = (categoryName || '').toLowerCase();
  const lowerTopic = (topicTitle || '').toLowerCase();
  
  if (lowerTopic.includes('calm') || lowerCategory.includes('calm') || lowerCategory.includes('mental')) {
    return CalmDownIcon;
  }
  if (lowerTopic.includes('motivat') || lowerCategory.includes('motivat') || lowerCategory.includes('physical')) {
    return MotivateIcon;
  }
  if (lowerTopic.includes('relationship') || lowerCategory.includes('relationship')) {
    return RelationshipsIcon;
  }
  return MeditationIcon;
};

// Get color based on category/topic
const getCategoryColor = (categoryName, topicTitle) => {
  const lowerCategory = (categoryName || '').toLowerCase();
  const lowerTopic = (topicTitle || '').toLowerCase();
  
  // Check topic title first
  for (const [key, color] of Object.entries(CATEGORY_COLORS)) {
    if (lowerTopic.includes(key) || lowerCategory.includes(key)) {
      return color;
    }
  }
  
  return '#1E69FF'; // Default blue
};

/**
 * TopicOptionsModal - Displays session options for a selected topic
 * 
 * @param {boolean} isOpen - Whether the modal is open
 * @param {function} onClose - Callback when modal is closed
 * @param {object} topic - The selected topic object with configuration
 * @param {function} onSelectOption - Callback when an option is selected
 * @param {string} platform - 'web' or 'mobile' (default: 'web')
 * @param {string} sessionType - 'voice' or 'keyboard'
 * @param {function} onStartSession - Callback to start session with selected option
 * @param {function} onBack - Callback when back button is clicked (returns to topics list)
 */
const TopicOptionsModal = ({
  isOpen,
  onClose,
  topic,
  onSelectOption,
  platform = 'web',
  sessionType = 'voice',
  onStartSession,
  onBack,
}) => {
  // Get session options from topic configuration
  const sessionOptions = useMemo(() => {
    if (!topic?.configuration) return [];
    
    const platformConfig = topic.configuration[platform];
    if (!platformConfig) return [];
    
    const modeConfig = platformConfig[sessionType];
    if (!modeConfig?.options) return [];
    
    return modeConfig.options;
  }, [topic, platform, sessionType]);

  // Get AI configuration for the session type
  const aiConfig = useMemo(() => {
    if (!topic?.ai_configuration) return null;
    return topic.ai_configuration[sessionType] || null;
  }, [topic, sessionType]);

  const handleOptionClick = (option) => {
    // Get conversation starter and prompt using option.id
    const conversationStarter = aiConfig?.conversation_starters?.[option.id] || null;
    const prompt = aiConfig?.prompts?.[option.id] || null;

    // Build session descriptor
    const sessionDescriptor = {
      topicId: topic.topic_id,
      topicTitle: topic.title,
      categoryId: topic.categoryId,
      categoryName: topic.categoryName,
      optionId: option.id,
      optionTitle: option.title,
      durationSeconds: option.duration,
      conversationStarter: conversationStarter,
      prompt: prompt,
      aiConfig: aiConfig,
      mode: sessionType,
      platform: platform,
    };

    if (onSelectOption) {
      onSelectOption(sessionDescriptor);
    }

    if (onStartSession) {
      onStartSession(sessionDescriptor);
    }
  };

  // formatDuration is available for future use when duration display is needed
  // const formatDuration = (seconds) => {
  //   if (!seconds) return null;
  //   if (seconds < 60) return `${seconds} seconds`;
  //   const minutes = Math.floor(seconds / 60);
  //   return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
  // };

  // Get dynamic icon and color based on category
  const IconComponent = useMemo(() => {
    return getCategoryIcon(topic?.categoryName, topic?.title);
  }, [topic?.categoryName, topic?.title]);

  const themeColor = useMemo(() => {
    return getCategoryColor(topic?.categoryName, topic?.title);
  }, [topic?.categoryName, topic?.title]);

  if (!isOpen || !topic) return null;

  return (
    <div className="topic-options-overlay" onClick={onClose}>
      <div 
        className="topic-options-modal topic-options-slide-in" 
        onClick={(e) => e.stopPropagation()}
      >
        {/* Back button with circle */}
        {onBack && (
          <button className="topic-options-back" onClick={onBack}>
            <BackArrowIcon />
          </button>
        )}

        <div className="topic-options-header">
          <div className="topic-options-icon" style={{ color: themeColor }}>
            <IconComponent />
          </div>
          <h2 className="topic-options-title" style={{ color: themeColor }}>
            {topic.title}
          </h2>
          {topic.description && (
            <p className="topic-options-description">{topic.description}</p>
          )}
        </div>

        <div className="topic-options-content">
          {sessionOptions.length === 0 ? (
            <div className="topic-options-empty">
              <p>No session options available for this topic.</p>
              <button 
                className="topic-options-start-btn"
                onClick={() => handleOptionClick({ title: 'Open Session', duration: null })}
              >
                Start Session
              </button>
            </div>
          ) : (
            <div className="topic-options-list">
              {sessionOptions.map((option, index) => (
                <button
                  key={index}
                  className="topic-options-item"
                  onClick={() => handleOptionClick(option)}
                >
                  <span className="topic-options-item-title">{option.title}</span>
                  {option.subtext && (
                    <span className="topic-options-item-subtext">{option.subtext}</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default TopicOptionsModal;
